import uuid from 'react-native-uuid';
import Track from './Track';

class TrackFile {
    constructor(file, project) {
        this.trackId = uuid.v4();
        this.revId = uuid.v4();
        this.file = file;
        this.fileURL = URL.createObjectURL(file);

        if (file.name.includes('.')) {
            this.name = file.name.substring(0, file.name.lastIndexOf('.'));
        } else {
            this.name = file.name;
        }

        // Extract file type (extension)
        this.fileType = file.name.substring(file.name.lastIndexOf('.') + 1);
        this.size = file.size;
        this.lastModified = file.lastModified;
        this.project = project;
        this.uploadingProgress = 0;
    }

    async asTrack() {
        const duration = await this.getDuration();
        return new Track(
            this.trackId,
            this.revId,
            this.name,
            duration,
            10,
            false,
            this.project,
            this.fileURL
        );
    }

    // Per-file payload for the getGranteePutAudioURLs batch call.
    putItem() {
        return { trackId: this.trackId, audioId: this.revId, size: this.size };
    }

    // Per-file payload for the getTrackMetadataBatch call.
    async metadataItem() {
        return {
            trackId: this.trackId,
            revId: this.revId,
            name: this.name,
            fileType: this.fileType,
            duration: await this.getDuration(),
            size: this.size,
            lastModified: this.lastModified,
        };
    }

    getDuration() {
        if (!this._durationPromise) this._durationPromise = this._computeDuration();
        return this._durationPromise;
    }

    async _computeDuration() {
        // HTMLAudio -> WebAudio decodeAudioData -> AIFF COMM-chunk parser.
        // Default 0 if all fail so the upload still proceeds.
        const fromAudioElement = await new Promise((resolve) => {
            const audioElement = new Audio(this.fileURL);
            audioElement.addEventListener("loadedmetadata", () => {
                resolve(Number.isFinite(audioElement.duration) ? audioElement.duration : null);
            });
            audioElement.onerror = () => resolve(null);
        });
        if (fromAudioElement != null) return fromAudioElement;

        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (Ctx) {
                const ctx = new Ctx();
                try {
                    const buf = await this.file.arrayBuffer();
                    const decoded = await ctx.decodeAudioData(buf);
                    if (Number.isFinite(decoded?.duration)) return decoded.duration;
                } finally {
                    ctx.close?.();
                }
            }
        } catch (_) { /* fall through to AIFF parser */ }

        const ext = (this.file?.name?.split('.').pop() || '').toLowerCase();
        if (ext === 'aif' || ext === 'aiff' || ext === 'aifc') {
            try {
                // 64KB covers AIFFs with large preceding chunks (ANNO/APPL)
                // before COMM. Most files have COMM in the first KB.
                const head = await this.file.slice(0, 64 * 1024).arrayBuffer();
                const aiffDur = TrackFile._parseAIFFDuration(head);
                if (Number.isFinite(aiffDur)) return aiffDur;
            } catch (_) { /* fall through */ }
        }

        console.warn(`Could not determine duration for "${this.file?.name}"; defaulting to 0.`);
        return 0;
    }

    /// Parses the COMM chunk in an AIFF/AIFC FORM container and returns
    /// duration in seconds (numSampleFrames / sampleRate). Returns null
    /// if the bytes don't look like AIFF or COMM is absent in the slice.
    static _parseAIFFDuration(arrayBuffer) {
        const dv = new DataView(arrayBuffer);
        if (dv.byteLength < 12) return null;
        // "FORM" + size + "AIFF"|"AIFC"
        if (dv.getUint32(0) !== 0x464F524D) return null;
        const form = dv.getUint32(8);
        if (form !== 0x41494646 && form !== 0x41494643) return null;
        let off = 12;
        while (off + 8 <= dv.byteLength) {
            const id = dv.getUint32(off);
            const size = dv.getUint32(off + 4);
            if (id === 0x434F4D4D && off + 8 + 18 <= dv.byteLength) { // "COMM"
                const numSampleFrames = dv.getUint32(off + 8 + 2);
                const sampleRate = TrackFile._readIEEE754Extended(dv, off + 8 + 8);
                if (sampleRate > 0) return numSampleFrames / sampleRate;
                return null;
            }
            off += 8 + size + (size % 2); // chunks are word-aligned
        }
        return null;
    }

    /// 80-bit IEEE 754 extended-precision float — used by AIFF for
    /// sampleRate. 1 sign + 15 exponent + 64 mantissa.
    static _readIEEE754Extended(dv, offset) {
        const e0 = dv.getUint16(offset);
        const sign = (e0 & 0x8000) ? -1 : 1;
        const exp = e0 & 0x7FFF;
        const mantHigh = dv.getUint32(offset + 2);
        const mantLow = dv.getUint32(offset + 6);
        if (exp === 0 && mantHigh === 0 && mantLow === 0) return 0;
        if (exp === 0x7FFF) return sign * Infinity;
        const mantissa = mantHigh * 0x100000000 + mantLow;
        return sign * mantissa * Math.pow(2, exp - 16383 - 63);
    }

    // PUTs the file bytes to a signed URL minted by getGranteePutAudioURLs.
    // XHR (not fetch) for upload progress. Headers MUST match the v4
    // signature exactly or Storage rejects with 403.
    putBytes(put, progressCallback) {
        const { url, contentType, headers } = put || {};
        if (!url) return Promise.reject(new Error('No PUT URL returned.'));
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', url);
            xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');
            for (const [k, v] of Object.entries(headers || {})) xhr.setRequestHeader(k, v);
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) progressCallback(Math.round((e.loaded / e.total) * 100));
            };
            xhr.onerror = () => reject(new Error('Upload network error'));
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText?.slice(0, 200) || ''}`));
            };
            xhr.send(this.file);
        });
    }
}

export default TrackFile;